import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { PARTNER_BRAND } from '../../partnerDashboardTheme';

export const COMMUNITY_VIEWS = {
  OVERVIEW: 'overview',
  JOIN_REQUESTS: 'join-requests',
  EDIT: 'edit',
};

const outlinedButtonSx = {
  justifyContent: 'center',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  borderRadius: '10px',
  py: 1.1,
  px: 1.5,
  borderColor: PARTNER_BRAND,
  color: PARTNER_BRAND,
  '&:hover': {
    borderColor: 'primary.dark',
    bgcolor: 'rgba(224, 104, 10, 0.04)',
  },
};

const activeNavButtonSx = {
  ...outlinedButtonSx,
  bgcolor: (theme) => theme.portal?.accentSoft ?? theme.palette.action.selected,
  borderColor: PARTNER_BRAND,
  color: PARTNER_BRAND,
};

const CommunityManagementPanel = ({
  activeView = COMMUNITY_VIEWS.OVERVIEW,
  isArchived = false,
  requireJoinApproval = false,
  pendingJoinRequests = 0,
  restoring = false,
  onEditDetails,
  onViewCommunity,
  onJoinRequests,
  onArchiveCommunity,
  onRestoreCommunity,
}) => {
  const isOverviewActive = activeView === COMMUNITY_VIEWS.OVERVIEW;
  const isJoinRequestsActive = activeView === COMMUNITY_VIEWS.JOIN_REQUESTS;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        bgcolor: 'background.paper',
        p: 2,
        height: { xl: 'fit-content' },
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary', mb: 2 }}>
        Community Management
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ForumOutlinedIcon />}
          onClick={onViewCommunity}
          sx={isOverviewActive ? activeNavButtonSx : outlinedButtonSx}
          aria-current={isOverviewActive ? 'page' : undefined}
        >
          View Community
        </Button>

        {isArchived ? (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RestoreOutlinedIcon />}
            onClick={onRestoreCommunity}
            disabled={restoring}
            sx={outlinedButtonSx}
          >
            {restoring ? 'Restoring…' : 'Restore Community'}
          </Button>
        ) : (
          <>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={onEditDetails}
              sx={outlinedButtonSx}
            >
              Edit Details
            </Button>

            {requireJoinApproval ? (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GroupOutlinedIcon />}
                onClick={onJoinRequests}
                sx={isJoinRequestsActive ? activeNavButtonSx : outlinedButtonSx}
                aria-current={isJoinRequestsActive ? 'page' : undefined}
              >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  Join Requests
                  {pendingJoinRequests > 0 ? (
                    <Box
                      component="span"
                      sx={{
                        minWidth: 18,
                        height: 18,
                        px: 0.5,
                        borderRadius: '9px',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        lineHeight: '18px',
                        textAlign: 'center',
                      }}
                    >
                      {pendingJoinRequests}
                    </Box>
                  ) : null}
                </Box>
              </Button>
            ) : null}

            <Button
              fullWidth
              variant="outlined"
              startIcon={<ArchiveOutlinedIcon />}
              onClick={onArchiveCommunity}
              sx={outlinedButtonSx}
            >
              Archive Community
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CommunityManagementPanel;
